/**
 * @file approval-card.tsx
 * @module app/modules/service-request/components/approval-card
 * @description One request in the approver's view. It leads with what will happen, who asked, and
 * why, so the approver can decide without opening anything. Pending requests get Approve and
 * Decline; decided ones show their status and outcome instead.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import moment from 'moment';
import { Spinner } from 'react-bootstrap';
import { Check, LucideIcon, Ticket, X } from 'lucide-react';
import RequestOutcome, { formatRequestDate } from './request-outcome';
import RequestStatusBadge from './request-status-badge';
import { RequestFieldsDisplay } from './fields/request-fields';
import {
  ServiceRequest,
  ServiceRequestField,
  ServiceRequestStatus,
  ServiceRequestStep,
} from '../types/service-request.types';

type ApprovalCardProps = {
  request: ServiceRequest;
  fields?: ServiceRequestField[];
  /** The step running on this request right now, if any. */
  busyStep: ServiceRequestStep | null;
  /** A request is being changed; buttons wait for it. */
  disabled: boolean;
  /** Jira is set up and this role may use it. */
  canSendToJira: boolean;
  onApprove: (request: ServiceRequest) => void;
  onDecline: (request: ServiceRequest) => void;
  onSendToJira: (request: ServiceRequest) => void;
};

const ApprovalCard = ({
  request,
  fields,
  busyStep,
  disabled,
  canSendToJira,
  onApprove,
  onDecline,
  onSendToJira,
}: ApprovalCardProps) => {
  const { requestedBy, warehouse, requestReason, status } = request;
  const isPending = status === ServiceRequestStatus.Pending;
  const showSendToJira =
    canSendToJira &&
    !request.jiraTicket &&
    (isPending || status === ServiceRequestStatus.Failed);

  const stepIcon = (step: ServiceRequestStep, Icon: LucideIcon) =>
    busyStep === step ? (
      <Spinner animation="border" size="sm" aria-hidden="true" />
    ) : (
      <Icon size={16} aria-hidden="true" />
    );

  const sendToJiraButton = showSendToJira ? (
    <button
      type="button"
      className="uw-btn uw-btn-sm uw-btn-light btn btn-sm btn-light"
      disabled={disabled}
      aria-label={`Send ${request.refKey} to IT`}
      onClick={() => onSendToJira(request)}
    >
      {stepIcon('send-to-jira', Ticket)}
      Send to IT
    </button>
  ) : null;

  const requester = requestedBy.role?.name
    ? `${requestedBy.name} (${requestedBy.role.name})`
    : requestedBy.name;

  return (
    <article className="sr-approval">
      <div className="sr-approval__head">
        <div className="sr-approval__summary">
          <div className="sr-approval__title">
            {request.actionLabel}
            <span className="sr-approval__ref">{request.refKey}</span>
          </div>
          <div className="sr-approval__meta">
            <span>{requester}</span>
            {warehouse?.name ? <span>{warehouse.name}</span> : null}
            <span title={formatRequestDate(request.createdAt)}>
              {moment(request.createdAt).fromNow()}
            </span>
          </div>
        </div>
        {isPending ? (
          <div className="sr-actions">
            <button
              type="button"
              className="uw-btn uw-btn-sm uw-btn-light btn btn-sm btn-light sr-btn-decline"
              disabled={disabled}
              aria-label={`Decline ${request.refKey}`}
              onClick={() => onDecline(request)}
            >
              {stepIcon('decline', X)}
              Decline
            </button>
            {sendToJiraButton}
            <button
              type="button"
              className="uw-btn uw-btn-sm uw-btn-success btn btn-sm btn-success"
              disabled={disabled}
              aria-label={`Approve ${request.refKey}`}
              onClick={() => onApprove(request)}
            >
              {stepIcon('approve', Check)}
              Approve
            </button>
          </div>
        ) : (
          <div className="sr-actions">
            {sendToJiraButton}
            <RequestStatusBadge status={status} />
          </div>
        )}
      </div>

      <div className="sr-approval__body">
        {requestReason ? (
          <div className="sr-approval__row">
            <div className="sr-fields__label">Reason</div>
            <div>{requestReason}</div>
          </div>
        ) : null}
        <RequestFieldsDisplay
          payload={request.input}
          fields={fields}
          alwaysLabel
        />
      </div>

      <RequestOutcome request={request} />
    </article>
  );
};

export default ApprovalCard;
